import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';

const Booklist = () => {
  const [booklists, setBooklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');

  useEffect(() => {
    fetchBooklists();
  }, []);

  const fetchBooklists = async () => {
    try {
      const response = await fetch('/admin/api/booklist.php');
      const data = await response.json();
      setBooklists(data);
    } catch (error) {
      console.error('Error fetching booklists:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBooklists = booklists.filter(booklist => {
    const gradeMatch = selectedGrade === 'all' || booklist.grade === selectedGrade;
    const subjectMatch = selectedSubject === 'all' || booklist.subject === selectedSubject;
    return gradeMatch && subjectMatch;
  });

  const uniqueGrades = [...new Set(booklists.map(booklist => booklist.grade))];
  const uniqueSubjects = [...new Set(booklists.map(booklist => booklist.subject))];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Book Lists</h1>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
              Find the required textbooks and reference materials for each grade and subject. 
              Download comprehensive book lists to ensure you have all the necessary resources.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Grade
              </label>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Grades</option>
                {uniqueGrades.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Subjects</option>
                {uniqueSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Booklist Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {filteredBooklists.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="fas fa-books"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Book Lists Found</h3>
              <p className="text-gray-600">No book lists match your current filters.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBooklists.map((booklist) => (
              <div
                key={booklist.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      booklist.publish_status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {booklist.publish_status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(booklist.created_at)}
                    </span>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {booklist.title}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-graduation-cap w-4 h-4 mr-2"></i>
                      <span className="text-sm">Grade {booklist.grade}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-book w-4 h-4 mr-2"></i>
                      <span className="text-sm">{booklist.subject}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-calendar w-4 h-4 mr-2"></i>
                      <span className="text-sm">Academic Year: {booklist.academic_year}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {booklist.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      <i className="fas fa-file-pdf mr-1"></i>
                      {booklist.file_path ? 'PDF Available' : 'No File'}
                    </div>
                    
                    <div className="flex space-x-2">
                      {booklist.file_path && (
                        <a
                          href={`admin/${booklist.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                        >
                          <i className="fas fa-download mr-1"></i>Download
                        </a>
                      )}
                      <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors">
                        <i className="fas fa-eye mr-1"></i>View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Book Purchase Tips */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-shopping-cart text-indigo-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-indigo-900 mb-2">
                Book Purchase Tips
              </h3>
              <ul className="text-indigo-800 space-y-1">
                <li>• Purchase books from authorized school bookstores for best prices</li>
                <li>• Check for used book options to save money</li>
                <li>• Ensure you have the correct edition and ISBN numbers</li>
                <li>• Consider digital versions if available and preferred</li>
                <li>• Keep receipts for potential returns or exchanges</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Booklist;
